import api from './client';
import {get, patch, post, put} from './http';
import {type FlagColor} from './flags';

// SetItemFlagsRequest.MaxItems server-side: the most ids one setFlags call may carry.
export const ITEM_FLAGS_MAX_IDS = 1000;

// How many times a queued image (a 202 from /images/source) is asked for again before it is treated
// as missing. At the API's 2s Retry-After that is ~20s: several of ContentStorage's 5s polls.
export const STORED_IMAGE_MAX_WAITS = 10;

/**
Resolves `request` to its image, asking again while it answers 202: the URL is queued and
ContentStorage stores it within seconds. Rejects once it has waited `STORED_IMAGE_MAX_WAITS` times,
so a stuck fetch shows as missing rather than loading forever.
*/
export async function waitForStoredImage(
  request: () => Promise<{
    readonly status: number;
    readonly headers: Record<string, unknown>;
    readonly data: Blob;
  }>,
  sleep: (ms: number) => Promise<void> = async (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    })
): Promise<Blob> {
  for (let waits = 0; ; waits++) {
    const response = await request();
    if (response.status !== 202) {
      return response.data;
    }

    if (waits >= STORED_IMAGE_MAX_WAITS) {
      throw new Error('Image is not stored yet');
    }

    const retryAfter = Number(response.headers['retry-after']);
    await sleep((Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 2) * 1000);
  }
}

export type SubmissionStatusGroup = {
  status: string;
  policy_violations: string[];
  // Only ever set for the 'historic' status - the most recent matching historic row's own
  // delivered_at (or created_at, when the export carried no delivery date).
  submitted_at: string | null;
};

export type ResolveItemRequest = {
  item_id: string;
  external_id?: string | null;
  url?: string | null;
  title?: string | null;
  thumbnail_url?: string | null;
  published_at?: string | null;
  author_id?: string | null;
  author_name?: string | null;
  author_handle?: string | null;
  metadata?: unknown;
  affiliate?: string;
  platform?: string;
  submission_type?: string;
};

export type Item = {
  id: string;
  external_id: string;
  platform: string;
  content_type: string;
  title: string | null;
  url: string | null;
  thumbnail_url: string | null;
  // The thumbnail was replaced after the item was first collected - history at /items/{id}/thumbnails.
  thumbnail_changed?: boolean;
  // Our preserved copy of the thumbnail (/images/{sha}), for when the platform URL no longer loads.
  stored_thumbnail_sha256?: string | null;
  // Classified under a sensitive risk: every stored image was deleted and thumbnail_url is null.
  images_purged?: boolean;
  // Why: classified under a sensitive risk, or its image matches one an earlier purge removed.
  images_purged_reason?: 'sensitive_risk' | 'matches_purged_image' | null;
  author_handle: string | null;
  author_id: string | null;
  author_name: string | null;
  published_at: string | null;
  metadata: Record<string, unknown>;
  affiliate: string;
  created_at: string;
  submission_status: 'in hopper' | 'sent to client' | 'historic' | 'none' | null;
  submission_policy_violations: string[];
  submission_status_groups: SubmissionStatusGroup[];
  // The item's channel's own live submissions, apart from the item's - so a video can say "channel
  // submitted" without implying the video was. Absent when the channel has none (or on a client-built Item).
  channel_submission_status_groups?: SubmissionStatusGroup[];
  item_status: 'active' | 'dismissed' | null;
  is_new_item: boolean | null;
  // The signed-in analyst's own flag on this item - personal, and the only flag there is: every
  // submission of the item shows this same one (Submission.flag). The API always sends it (null when unflagged); it is absent only on an Item
  // the SPA built itself, which has no server row to hang a flag off, so absence means "can't flag".
  flag?: FlagColor | null;
  // The caller's own note on that flag, and when they flagged it. Personal like the flag.
  flag_note?: string | null;
  flagged_at?: string | null;
  // Other analysts' unexpired queue leases on this item, one per policy violation. Empty or absent when
  // nobody else is reviewing it; never includes the signed-in analyst's own.
  queue_leases?: ItemQueueLease[];
  // The latest RD evaluation for this item in the listed scope; null when none was recorded. Only
  // present on investigation-scoped list routes.
  rd_assessment?: RdEvaluation | null;
};

export type ItemQueueLease = {owner_name: string; policy_violation: string; expires_at: string};

export type ItemsQuery = {
  affiliate?: string;
  platform?: string;
  content_type?: string;
  q?: string;
  job_id?: string;
  folder_id?: string;
  // Scopes to one channel - the investigations UI's "Group by channel" fetches a channel's items
  // with this only once the analyst actually expands that group, instead of bulk-fetching every
  // item up front just to bucket them client-side.
  author_id?: string;
  // One channel as ListAllChannels keys it (author id, else handle, else name) - opens a channel
  // the Content page's Channels tab listed, including one with no author id.
  channel_key?: string;
  age_restricted?: boolean;
  channel_monetized?: boolean;
  verified?: boolean;
  made_for_kids?: boolean;
  is_short?: boolean;
  content_warning?: boolean;
  safe_for_minors?: boolean;
  thumbnail_changed?: boolean;
  new_only?: boolean;
  min_subscribers?: number;
  min_views?: number;
  min_pins?: number;
  min_boards?: number;
  min_saves?: number;
  last_post_since?: string;
  // Inclusive bounds on the video's length.
  min_duration_seconds?: number;
  max_duration_seconds?: number;
  // Whole UTC days, both inclusive, on the platform's upload date.
  published_since?: string;
  published_until?: string;
  // BCP-47 base subtags; an item matches when it declares any of them. Sent as a repeated key.
  language?: string[];
  // JSON array of {signal, values}; groups and values within each group match with OR.
  rd_filters?: string;
  // The caller's own flags: items flagged in any of these colours, or in any colour at all for
  // 'any'. Sent as a repeated key (flag=red&flag=blue).
  flag?: Array<FlagColor | 'any'>;
  sort?: ItemSort;
  page?: number;
  page_size?: number;
};

// Mirrors ItemsController.SortOrders. "published" is the platform's upload date (items without one
// sort last either way), "collected" is when PRI first saw the item, "updated" when a collection
// last re-saw and refreshed it.
export const ITEM_SORTS = [
  'collected_desc',
  'collected_asc',
  'published_desc',
  'published_asc',
  'updated_desc',
  'updated_asc',
  // The caller's own flag time - only offered under a flag filter (the server orders unflagged rows last).
  'flagged_desc'
] as const;
export type ItemSort = (typeof ITEM_SORTS)[number];
export const DEFAULT_ITEM_SORT: ItemSort = 'collected_desc';

// Same shape as the filter subset ItemsQuery accepts, minus paging/scoping - what's left after a
// job/channel is already chosen. Shared by ListJobChannels's own filters.
export type ItemFilters = Pick<
  ItemsQuery,
  | 'q'
  | 'age_restricted'
  | 'channel_monetized'
  | 'verified'
  | 'made_for_kids'
  | 'is_short'
  | 'content_warning'
  | 'safe_for_minors'
  | 'thumbnail_changed'
  | 'new_only'
  | 'min_subscribers'
  | 'min_views'
  | 'min_pins'
  | 'min_boards'
  | 'min_saves'
  | 'last_post_since'
  | 'min_duration_seconds'
  | 'max_duration_seconds'
  | 'published_since'
  | 'published_until'
  | 'language'
  | 'rd_filters'
>;

export type ItemsPage = {
  results: Item[];
  page: number;
  page_size: number;
  has_more: boolean;
  total: number;
  // Set when `total` is a floor rather than the exact count: a folder's count stops at a cap.
  total_capped?: boolean;
};

// A page fetched without its total (no_count), for a caller that asks itemsApi.count for it.
export type ItemsPageWithoutTotal = Omit<ItemsPage, 'total' | 'total_capped'>;

export type ItemsCount = {
  total: number;
  // Set when `total` is a floor rather than the exact count: a folder's count stops at a cap.
  total_capped: boolean;
};

export type BulkSetJobItemStatusResponse = {
  updated_count: number;
};

// One channel's counts within a job - mirrors groupItemsByChannel's own ChannelGroup shape, minus
// `items` (fetched separately, only once the analyst expands the group).
export type ChannelAggregate = {
  key: string;
  author_id: string | null;
  label: string;
  total: number;
  dismissed_count: number;
  classified_count: number;
  // Overlap between dismissed_count and classified_count - classifying an item and then
  // dismissing it is a normal flow, so the two aren't disjoint.
  dismissed_and_classified_count: number;
  submission_status_groups: SubmissionStatusGroup[];
};

export type ChannelAggregateList = {
  results: ChannelAggregate[];
};

// Signals and decisions are the evaluator's raw objects, kept as emitted.
export type RdEvaluation = {
  evaluation_id: string;
  job_id: string;
  evaluated_at: string;
  signals: unknown[];
  decisions: unknown[];
};

export type RdEvaluationPage = {
  results: RdEvaluation[];
  page: number;
  page_size: number;
  has_more: boolean;
  total: number;
};

export type RdSignalNameOption = {name: string; count: number};
export type RdSignalValueOption = {value_json: string; count: number};

export type RdOptionsPage<T> = {
  results: T[];
  page: number;
  page_size: number;
  has_more: boolean;
};

export type RdOptionsScope = {kind: 'job' | 'folder'; id: string};

const RD_OPTIONS_SEGMENT = {job: 'jobs', folder: 'folders'} as const;

export type RdOptionsParameters = ItemFilters & {
  name?: string;
  search?: string;
  page?: number;
  page_size?: number;
};

export type FollowerGraph = {
  nodes: Array<{item: Item; degree: 0 | 1 | 2}>;
  edges: Array<{follower_item_id: string; followed_item_id: string}>;
  // Second-hop crawls still running - the view keeps polling while this is above 0.
  pending_child_jobs: number;
  truncated: boolean;
  // Followers whose own followers were crawled, of the investigation's first-degree followers - fewer
  // when the cap on second-hop crawls was reached.
  expanded_followers: number;
  first_degree_followers: number;
};

export type ItemThumbnailVersion = {
  id: string;
  source_url: string;
  sha256: string;
  is_original: boolean;
  is_current: boolean;
  observed_at: string;
  image_url: string;
};

export const itemsApi = {
  thumbnails: async (id: string) =>
    get<{versions: ItemThumbnailVersion[]}>(`/items/${id}/thumbnails`),

  // Fetched through the authenticated client and handed to <img> as a blob URL: an <img src> cannot
  // carry the bearer token, and the bucket behind it is never public. Content-addressed, so the API
  // marks it immutable and the browser's HTTP cache answers repeat requests.
  async storedImage(sha256: string) {
    const {data} = await api.get<Blob>(`/images/${sha256}`, {responseType: 'blob'});
    return data;
  },

  // Our stored copy of whatever a platform image URL served when ContentStorage last fetched it; a 404
  // when there is none. Lets every platform image load from us first, whether or not the caller knows
  // its hash. A URL queued ahead of collection work (a live Pinterest lookup) is waited for.
  async storedImageBySource(url: string) {
    return waitForStoredImage(async () =>
      api.get<Blob>('/images/source', {params: {url}, responseType: 'blob'})
    );
  },

  list: async (parameters?: ItemsQuery) => get<ItemsPage>('/items', parameters),

  // The page alone. On a big folder the count is slower than the page, so a caller that shows both
  // fetches the total from `count` in parallel instead of waiting on it here.
  listWithoutTotal: async (parameters?: ItemsQuery) =>
    get<ItemsPageWithoutTotal>('/items', {...parameters, no_count: true}),

  // The total `list` would report for the same filters - see ItemsController.CountItems.
  count: async (parameters?: Omit<ItemsQuery, 'sort' | 'page' | 'page_size'>) =>
    get<ItemsCount>('/items/count', parameters),

  listForJob: async (
    jobId: string,
    parameters?: ItemFilters & {
      author_id?: string;
      sort?: ItemSort;
      page?: number;
      page_size?: number;
    }
  ) => get<ItemsPage>(`/jobs/${jobId}/items`, parameters),

  // Per-channel counts for the whole job, not just whatever's currently paged into the client -
  // see ItemsController.ListChannels.
  listChannelsForJob: async (jobId: string, parameters?: ItemFilters) =>
    get<ChannelAggregateList>(`/jobs/${jobId}/channels`, parameters),

  // The Content page's Channels tab: the largest channels across every item, under the same
  // filters as the item list. `limit` caps how many channels come back - see
  // ItemsController.ListAllChannels.
  listAllChannels: async (
    parameters?: Omit<ItemsQuery, 'page' | 'page_size' | 'sort' | 'channel_key'> & {limit?: number}
  ) => get<ChannelAggregateList>('/items/channels', parameters),

  // Who follows whom across a follower investigation - see ItemsController.GetFollowerGraph.
  followerGraph: async (jobId: string) => get<FollowerGraph>(`/jobs/${jobId}/follower-graph`),

  // Same aggregate, scoped to every job in a folder instead of one job.
  listChannelsForFolder: async (folderId: string, parameters?: ItemFilters) =>
    get<ChannelAggregateList>(`/folders/${folderId}/channels`, parameters),

  rdEvaluations: async (jobId: string, itemId: string, page: number, pageSize = 20) =>
    get<RdEvaluationPage>(`/jobs/${jobId}/items/${itemId}/rd-evaluations`, {
      page,
      page_size: pageSize
    }),

  rdSignalNames: async (scope: RdOptionsScope, parameters?: RdOptionsParameters) =>
    get<RdOptionsPage<RdSignalNameOption>>(
      `/${RD_OPTIONS_SEGMENT[scope.kind]}/${scope.id}/rd-signal-options`,
      parameters
    ),

  rdSignalValues: async (
    scope: RdOptionsScope,
    name: string,
    parameters?: Omit<RdOptionsParameters, 'name'>
  ) =>
    get<RdOptionsPage<RdSignalValueOption>>(
      `/${RD_OPTIONS_SEGMENT[scope.kind]}/${scope.id}/rd-signal-options`,
      {...parameters, name}
    ),

  // Sets the caller's own flag on every listed item, or clears it with null. All-or-nothing
  // server-side: one unknown id and nothing is written.
  setFlags: async (itemIds: string[], color: FlagColor | null, note?: string | null) =>
    put<{item_ids: string[]; color: FlagColor | null; note?: string}>('/items/flags', {
      item_ids: itemIds,
      color,
      // Omitted unless given: a recolour without a note keeps the one already there.
      ...(note ? {note} : {})
    }),

  // Sets (or, blank, clears) the caller's note on their flag for one item. 404 if it isn't flagged.
  setFlagNote: async (itemId: string, note: string | null) =>
    put<{note: string | null}>(`/items/${itemId}/flag/note`, {note}),

  // Makes a Single Submission row a real item so it can be flagged without being submitted. Returns
  // the id to use, which differs from `id` when the video was already known.
  resolve: async (body: ResolveItemRequest) =>
    post<ResolveItemRequest, {item_id: string}>('/items/resolve', body),

  setJobItemStatus: async (jobId: string, itemId: string, status: 'active' | 'dismissed') =>
    patch<{status: string}>(`/jobs/${jobId}/items/${itemId}/status`, {status}),

  // One request for the whole batch instead of one PATCH per item - the bulk-selection bar can
  // hand this hundreds of ids at once.
  bulkSetJobItemStatus: async (jobId: string, itemIds: string[], status: 'active' | 'dismissed') =>
    patch<{item_ids: string[]; status: string}, BulkSetJobItemStatusResponse>(
      `/jobs/${jobId}/items/status`,
      {item_ids: itemIds, status}
    ),

  // "Dismiss channel" without requiring every item in it to already be loaded - one UPDATE server
  // side, keyed by author_id rather than an id list the client may not have fetched yet.
  bulkSetJobItemStatusByAuthor: async (
    jobId: string,
    authorId: string,
    status: 'active' | 'dismissed'
  ) =>
    patch<{author_id: string; status: string}, BulkSetJobItemStatusResponse>(
      `/jobs/${jobId}/items/status`,
      {author_id: authorId, status}
    ),

  // Folder-scoped counterparts: dismiss/restore across every investigation in the folder at once -
  // see JobsController.BulkSetFolderItemStatus.
  bulkSetFolderItemStatus: async (
    folderId: string,
    itemIds: string[],
    status: 'active' | 'dismissed'
  ) =>
    patch<{item_ids: string[]; status: string}, BulkSetJobItemStatusResponse>(
      `/folders/${folderId}/items/status`,
      {item_ids: itemIds, status}
    ),

  bulkSetFolderItemStatusByAuthor: async (
    folderId: string,
    authorId: string,
    status: 'active' | 'dismissed'
  ) =>
    patch<{author_id: string; status: string}, BulkSetJobItemStatusResponse>(
      `/folders/${folderId}/items/status`,
      {author_id: authorId, status}
    )
};
